import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { GroceryListDetail, ReceiptScanResponse } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Section } from '../../components/Section';
import { Skeleton } from '../../components/Skeleton';
import { ScanReceiptButton } from '../../components/groceries/ScanReceiptButton';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { formatPriceCents, isChecked } from './display';
import { groceryKeys } from './queries';
import {
  applyReceiptResult,
  applySummary,
  buildApplyRequest,
  claimedItems,
  duplicateItemIds,
  initialLineStates,
  invalidPriceLines,
  lineOptions,
  type ReceiptChoice,
  type ReceiptLineState,
  type ReceiptNavState,
} from './receipt';

/**
 * `/groceries/:id/receipt` — review a scanned receipt (D34): each receipt
 * line with its price and the list row it paid for, the rows still unmatched,
 * and one "Apply" that checks them all off. The proposal arrives in the
 * navigation state from `ScanReceiptButton`; nothing is stored server-side,
 * so a reload offers a fresh scan.
 */
export function ReceiptReviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const proposal = (location.state as ReceiptNavState | null)?.proposal;

  const detail = useQuery({
    queryKey: groceryKeys.detail(id),
    queryFn: () => api.groceries.get(id),
    enabled: id !== '',
  });

  if (id === '' || detail.isPending) {
    return (
      <AppShell>
        <Skeleton lines={4} />
      </AppShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AppShell title="Scan a receipt">
        <Alert tone="error">{messageForError(detail.error)}</Alert>
        <BackLink listId={id} />
      </AppShell>
    );
  }
  if (!proposal) {
    return (
      <AppShell title="Scan a receipt">
        <div className="flex flex-col gap-4">
          <BackLink listId={id} />
          <Section title="Scan a receipt" accent="groceries">
            <p className="text-sm text-muted">
              Take a photo of the whole receipt. It is read by Anthropic’s Claude to fill in prices
              and is never saved. You review every match before anything is checked off.
            </p>
            <ScanReceiptButton listId={id} variant="primary" replace />
          </Section>
        </div>
      </AppShell>
    );
  }
  // Keyed by history entry so a re-scan starts the review afresh.
  return <Review key={location.key} listId={id} detail={detail.data} proposal={proposal} />;
}

function BackLink({ listId }: { listId: string }) {
  return (
    <Link
      to={`/groceries/${listId}`}
      className="inline-flex min-h-11 items-center text-sm font-medium text-brand underline-offset-2 hover:underline"
    >
      ← Back to the list
    </Link>
  );
}

function Review({
  listId,
  detail,
  proposal,
}: {
  listId: string;
  detail: GroceryListDetail;
  proposal: ReceiptScanResponse;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [states, setStates] = useState<ReceiptLineState[]>(() => initialLineStates(proposal));
  // Stable for this review, so a retried Apply cannot check anything off twice.
  const [keyPrefix] = useState(() => `rcpt-${Math.random().toString(36).slice(2, 10)}`);
  const { store } = proposal;
  const storeDiffers = store.matchedStoreId !== null && store.matchedStoreId !== store.listStoreId;
  const [useReceiptStore, setUseReceiptStore] = useState(storeDiffers);

  const unchecked = useMemo(() => detail.items.filter((i) => !isChecked(i)), [detail.items]);
  const claimed = claimedItems(states);
  const duplicates = duplicateItemIds(states);
  const badPrices = invalidPriceLines(states);
  const summary = applySummary(states);
  const stillUnmatched = unchecked.filter((i) => !claimed.has(i.id));

  const setLine = (index: number, patch: Partial<ReceiptLineState>) =>
    setStates((all) => all.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const apply = useMutation({
    mutationFn: () =>
      api.groceries.receipt.apply(
        listId,
        buildApplyRequest(proposal, states, detail.items, {
          storeId: storeDiffers && useReceiptStore ? store.matchedStoreId : undefined,
          keyPrefix,
        }),
      ),
    onSuccess: async (result) => {
      qc.setQueryData<GroceryListDetail>(groceryKeys.detail(listId), (old) =>
        old ? applyReceiptResult(old, result) : old,
      );
      void qc.invalidateQueries({ queryKey: groceryKeys.all, refetchType: 'none' });
      const n = result.checked + result.added;
      await navigate(`/groceries/${listId}`, {
        replace: true,
        state: {
          toast: `Checked off ${n} ${n === 1 ? 'item' : 'items'} from the receipt${
            result.added ? ` (${result.added} added to the list)` : ''
          }.`,
        },
      });
    },
  });

  const r = proposal.receipt;
  const printed = r.subtotalCents ?? r.totalCents;
  const canApply =
    summary.count > 0 && duplicates.length === 0 && badPrices.length === 0 && !apply.isPending;

  const applyButton = (
    <Button disabled={!canApply} onClick={() => apply.mutate()}>
      {apply.isPending
        ? 'Applying…'
        : summary.count === 0
          ? 'Nothing to apply'
          : `Check off ${summary.count} ${summary.count === 1 ? 'item' : 'items'} · ${formatPriceCents(summary.cents)}`}
    </Button>
  );

  return (
    <AppShell width="wide">
      <div className="flex flex-col gap-4">
        <div className="card p-4 sm:p-5">
          <BackLink listId={listId} />
          <h1 className="text-xl font-bold text-brand sm:text-2xl">Review receipt</h1>
          <p className="mt-1 flex flex-wrap gap-x-2 text-sm text-muted">
            {r.storeName ? <span>{r.storeName}</span> : null}
            {r.purchasedAt ? (
              <>
                <span aria-hidden>·</span>
                <span>{r.purchasedAt.slice(0, 10)}</span>
              </>
            ) : null}
            <span aria-hidden>·</span>
            <span>
              {proposal.lines.length} lines · {formatPriceCents(r.lineSumCents)}
              {printed !== null ? ` (receipt says ${formatPriceCents(printed)})` : ''}
            </span>
          </p>
          <div className="mt-3 flex flex-col gap-2">
            {proposal.warnings.map((w) => (
              <Alert key={w} tone="warning">
                {w}
              </Alert>
            ))}
            {storeDiffers ? (
              <label className="flex min-h-11 items-center gap-3 rounded-lg border border-line px-3 text-sm text-ink">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={useReceiptStore}
                  onChange={(e) => setUseReceiptStore(e.target.checked)}
                />
                <span>
                  Record these prices at <strong>{store.matchedStoreName}</strong>
                  {store.listStoreId ? ' instead of this list’s store' : ''}
                </span>
              </label>
            ) : null}
            {applyButton}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Section title="Receipt lines" accent="groceries">
            <ol className="flex flex-col gap-3">
              {proposal.lines.map((line, i) => {
                const s = states[i]!;
                const selectId = `receipt-line-${i}`;
                const dup = s.choice.startsWith('item:') && duplicates.includes(s.choice.slice(5));
                return (
                  <li
                    key={line.index}
                    className={`rounded-xl border p-3 ${
                      s.choice === 'ignore'
                        ? 'border-line/60 bg-surface/60'
                        : 'border-line bg-surface'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="min-w-0 break-words font-mono text-sm text-ink">
                        {line.rawText}
                      </span>
                      <label className="flex shrink-0 items-center gap-1 text-sm text-muted">
                        <span aria-hidden>$</span>
                        <span className="sr-only">Price for {line.rawText}</span>
                        <input
                          inputMode="decimal"
                          value={s.price}
                          onChange={(e) => setLine(i, { price: e.target.value })}
                          aria-invalid={badPrices.includes(i) ? true : undefined}
                          className={`min-h-11 w-24 rounded-lg border bg-surface px-2 text-right text-base text-ink ${
                            badPrices.includes(i) ? 'border-fail' : 'border-line'
                          }`}
                        />
                      </label>
                    </div>
                    {line.discountCents ? (
                      <p className="mt-1 text-xs text-muted">
                        Includes {formatPriceCents(line.discountCents)} off
                      </p>
                    ) : null}
                    <label htmlFor={selectId} className="sr-only">
                      List item for {line.rawText}
                    </label>
                    <select
                      id={selectId}
                      value={s.choice}
                      onChange={(e) => setLine(i, { choice: e.target.value as ReceiptChoice })}
                      aria-invalid={dup ? true : undefined}
                      className={`mt-2 min-h-11 w-full rounded-lg border bg-surface px-3 text-base text-ink ${
                        dup ? 'border-fail' : 'border-line'
                      }`}
                    >
                      {lineOptions(line, unchecked, claimed).map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {dup ? (
                      <p role="alert" className="mt-1 text-sm text-fail">
                        Another line is matched to this item too.
                      </p>
                    ) : null}
                    {s.choice === 'new' ? (
                      <label className="mt-2 flex flex-col gap-1 text-sm font-medium text-ink">
                        New item name
                        <input
                          value={s.newName}
                          onChange={(e) => setLine(i, { newName: e.target.value })}
                          maxLength={120}
                          className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base font-normal text-ink"
                        />
                      </label>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </Section>

          <Section
            title="Still on the list"
            description="Items no receipt line is matched to stay unchecked."
            accent="neutral"
          >
            {stillUnmatched.length === 0 ? (
              <p className="text-sm text-muted">Every item on the list is matched.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {stillUnmatched.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-lg bg-brand-soft/50 px-3 py-2 text-sm text-ink"
                  >
                    {item.productLabel ?? item.ingredientName}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {apply.isError ? <Alert tone="error">{messageForError(apply.error)}</Alert> : null}
        {applyButton}
      </div>
    </AppShell>
  );
}
